
"use client";

import { useRef, useState } from "react";
import {
  Download,
  Eye,
  FileText,
  Upload,
  LoaderCircle,
  X,
} from "lucide-react";
import type { ProfileResumeData } from "../../../types/candidateProfile.types";

interface ResumeCardProps {
  resume: ProfileResumeData | null;
  onManage?: () => void;
  onUpload?: (file: File) => Promise<void>;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];

export default function ResumeCard({
  resume,
  onManage,
  onUpload,
}: ResumeCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setError("");
    setSuccess("");

    const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;

    if (
      !ALLOWED_EXTENSIONS.includes(extension) ||
      !ALLOWED_FILE_TYPES.includes(file.type)
    ) {
      setError("Please upload a PDF, DOC, or DOCX file.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("Resume size must not exceed 5 MB.");
      event.target.value = "";
      return;
    }

    if (!onUpload) {
      setError("Resume upload is not connected yet.");
      event.target.value = "";
      return;
    }

    try {
      setUploading(true);

      await onUpload(file);

      setSuccess("Resume uploaded successfully.");
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Resume upload failed. Please try again.";

      setError(message);
    } finally {
      setUploading(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleUploadClick = () => {
    if (uploading) return;

    if (onManage) {
      onManage();
      return;
    }

    fileInputRef.current?.click();
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
          <FileText className="text-red-600" size={20} />
        </div>

        <div>
          <h2 className="text-lg font-semibold">Resume</h2>
          <p className="text-sm text-slate-500">Latest Resume</p>
        </div>
      </div>

      {resume ? (
        <>
          <div className="mt-5 rounded-xl border border-dashed border-slate-300 p-4">
            <h3 className="break-all font-medium">{resume.fileName}</h3>

            <p className="mt-1 text-xs text-slate-500">
              {resume.fileType} • {resume.fileSize}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={resume.fileUrl || undefined}
                target="_blank"
                rel="noreferrer"
                aria-disabled={!resume.fileUrl}
                onClick={(event) => {
                  if (!resume.fileUrl) event.preventDefault();
                }}
                className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-slate-50"
              >
                <Eye size={15} />
                Preview
              </a>

              <a
                href={resume.fileUrl || undefined}
                download
                aria-disabled={!resume.fileUrl}
                onClick={(event) => {
                  if (!resume.fileUrl) event.preventDefault();
                }}
                className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-slate-50"
              >
                <Download size={15} />
                Download
              </a>
            </div>
          </div>

          <div className="mt-5">
            <div className="mb-2 flex justify-between text-sm">
              <span>ATS Score</span>
              <span className="font-semibold text-green-600">
                {resume.atsScore}%
              </span>
            </div>

            <div className="h-2 rounded-full bg-slate-200">
              <div
                className="h-full rounded-full bg-green-500 transition-all"
                style={{
                  width: `${Math.min(
                    100,
                    Math.max(0, resume.atsScore || 0)
                  )}%`,
                }}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="mt-5 rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
          No resume uploaded yet
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        onChange={handleFileChange}
        className="hidden"
        disabled={uploading}
      />

      <button
        type="button"
        onClick={handleUploadClick}
        disabled={uploading}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2 text-sm text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {uploading ? (
          <>
            <LoaderCircle size={16} className="animate-spin" />
            Uploading Resume...
          </>
        ) : (
          <>
            <Upload size={16} />
            {resume ? "Manage Resume" : "Upload Resume"}
          </>
        )}
      </button>

      {error && (
        <div
          role="alert"
          className="mt-3 flex items-start justify-between gap-2 rounded-lg bg-red-50 p-3 text-xs text-red-600"
        >
          <span>{error}</span>

          <button
            type="button"
            onClick={() => setError("")}
            aria-label="Dismiss error"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {success && (
        <p
          role="status"
          className="mt-3 rounded-lg bg-green-50 p-3 text-xs text-green-700"
        >
          {success}
        </p>
      )}

      <p className="mt-2 text-xs text-slate-500">
        Supported formats: PDF, DOC, DOCX. Maximum size: 5 MB.
      </p>
    </div>
  );
}
